import {
  type RidePassengerInfo,
  usePassengerAuthentications,
} from '@backoffice-frontend/ad-passenger-list'
import { FccPassengerCheckInStatus } from '@backoffice-frontend/graphql/subscription'
import { useMoiaErrorNotification } from '@backoffice-frontend/patterns'
import { useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { AuthenticationStatus } from '../PassengerCard/PassengerCard'
import type {
  AdVehicleStreamPassengerCardCustomerInfoFragment,
  AdVehicleStreamPassengerCardSeatInfoFragment,
  AdVehicleStreamPassengerCardStopInfoFragment,
} from '../PassengerCard/PassengerCard.hook'
import { BoardingPassengerCard } from './BoardingPassengerCard'
import { usePendingTimeout } from './usePendingTimeout'

const FccPassengerCheckInStatusEnumToAuthenticationStatus: Record<
  FccPassengerCheckInStatus,
  AuthenticationStatus
> = {
  [FccPassengerCheckInStatus.ManualPassengerCheckInPending]:
    AuthenticationStatus.MANUAL_PASSENGER_CHECK_IN_PENDING,
  [FccPassengerCheckInStatus.ManualNoShowRequested]:
    AuthenticationStatus.MANUAL_NO_SHOW_REQUESTED,
  [FccPassengerCheckInStatus.NoShowTriggered]:
    AuthenticationStatus.NO_SHOW_TRIGGERED,
  [FccPassengerCheckInStatus.InProgress]: AuthenticationStatus.IN_PROGRESS,
  [FccPassengerCheckInStatus.Confirmed]: AuthenticationStatus.CONFIRMED,
  [FccPassengerCheckInStatus.Cancelled]: AuthenticationStatus.CANCELLED,
  [FccPassengerCheckInStatus.ManualNoShowPending]:
    AuthenticationStatus.MANUAL_NO_SHOW_PENDING,
}

type BoardingPassengerWithAuthenticationProps = {
  tripId: string
  vehicleId: string
  customer: AdVehicleStreamPassengerCardCustomerInfoFragment | null
  ridePassengerInfo?: RidePassengerInfo | null
  waypointStop: AdVehicleStreamPassengerCardStopInfoFragment | null
  seats: AdVehicleStreamPassengerCardSeatInfoFragment
  eta: Date
  isBoarded: boolean
  isNoShow: boolean
}
export const BoardingPassengerCardWithAuthentication = ({
  tripId,
  vehicleId,
  customer,
  ridePassengerInfo,
  waypointStop,
  seats,
  eta,
  isBoarded,
  isNoShow,
}: BoardingPassengerWithAuthenticationProps) => {
  const { t } = useTranslation()
  const { enqueueMoiaErrorNotification } = useMoiaErrorNotification()
  const { getPassengerAuthentication, getPassengerAuthenticationError } =
    usePassengerAuthentications()
  const subscriptionStatus = getPassengerAuthentication(tripId)
  const authenticationError = getPassengerAuthenticationError(tripId)
  const previousErrorRef = useRef(authenticationError)

  const mappedStatus = subscriptionStatus
    ? FccPassengerCheckInStatusEnumToAuthenticationStatus[subscriptionStatus]
    : AuthenticationStatus.UPCOMING

  const isPending =
    mappedStatus === AuthenticationStatus.MANUAL_PASSENGER_CHECK_IN_PENDING ||
    mappedStatus === AuthenticationStatus.MANUAL_NO_SHOW_PENDING

  const showErrorNotification = useCallback(() => {
    enqueueMoiaErrorNotification(
      t('adFleetMap_toast_passengerCheckInFailed_label'),
    )
  }, [enqueueMoiaErrorNotification, t])

  const { pendingTimedOut, clearPendingTimeout } = usePendingTimeout({
    isPending,
    onTimeout: showErrorNotification,
  })

  useEffect(() => {
    if (
      authenticationError &&
      authenticationError !== previousErrorRef.current
    ) {
      clearPendingTimeout()
      showErrorNotification()
    }
    previousErrorRef.current = authenticationError
  }, [authenticationError, clearPendingTimeout, showErrorNotification])

  const authenticationStatus =
    pendingTimedOut && isPending
      ? AuthenticationStatus.IN_PROGRESS
      : mappedStatus

  return (
    <BoardingPassengerCard
      tripId={tripId}
      vehicleId={vehicleId}
      customer={customer}
      ridePassengerInfo={ridePassengerInfo}
      waypointStop={waypointStop}
      seats={seats}
      eta={eta}
      authenticationStatus={authenticationStatus}
      isNoShow={isNoShow}
      isBoarded={isBoarded}
    />
  )
}
